import { useContext, useRef, useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Form, Input, Button, Checkbox } from 'antd'
import { useSelector } from 'react-redux'

import { AuthContext } from '../../Providers/AuthProvider'
import { personnalSso } from '../../utils/api/auth'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'

const FormItem = Form.Item

const LoginForm = ({ form, loginPending, prefilledEmail, onLogin, onPasswordResetClick }) => {
  const { redirectToSSOLoginPage } = useContext(AuthContext)

  const t = useSelector(selectTranslate)

  const [isLocalAccount, setIsLocalAccount] = useState(false)
  const [lookupPending, setLookupPending] = useState(false)
  // Email already routed at step 1: the form only goes back to the "email" step when the address
  // really changes. The browser password manager sometimes rewrites the email after the routing;
  // without this guard the form would jump back to step 1 while being autofilled.
  const routedEmailRef = useRef(null)

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!isLocalAccount) {
      // "Email" step: only the email is validated. The password field is mounted from the very
      // first render (see below) but still hidden, so its `required` rule must not block the
      // routing.
      form.validateFields(['username'], async (err, values) => {
        // The email is required (field rule): when the validation fails or the field is empty, antd
        // displays the message and no routing happens — without this guard values.username is
        // undefined and values.username.trim() throws a TypeError (spinner stuck).
        if (err || !values.username || values.username.trim().length === 0) {
          return
        }

        // Routing: only external accounts (Account.ssoInstitution) go through the SSO; every other
        // one (including unknown accounts, to prevent enumeration) stays on the local login.
        setLookupPending(true)

        try {
          const email = values.username.trim()
          const response = await personnalSso(email)

          if (response.personalSso) {
            redirectToSSOLoginPage(email)
          } else {
            routedEmailRef.current = email.toLowerCase()
            setIsLocalAccount(true)
          }
        } catch (e) {
          form.setFields({
            username: {
              value: values.username,
              errors: [new Error(t('error_handler.unknown_error'))]
            }
          })
        } finally {
          // Always release the spinner, even on an exception (otherwise the button stays loading).
          setLookupPending(false)
        }
      })

      return
    }

    form.validateFields((err, values) => {
      if (!err && values.username && values.username.length > 0 && values.password && values.password.length > 0) {
        // Trimmed like at the routing step: an email copy-pasted with a trailing space (or typed
        // with one by a mobile keyboard) must authenticate against the same account.
        onLogin({ ...values, username: values.username.trim() })
      }
    })
  }

  const handleForgotPassword = (e) => {
    e.preventDefault()
    onPasswordResetClick()
  }

  const handleEmailChange = (e) => {
    // Same address (typically a rewrite by the autofill): stay on the password step, otherwise the
    // field vanishes at the very moment the browser fills it.
    if (routedEmailRef.current !== null && e.target.value.trim().toLowerCase() === routedEmailRef.current) {
      return
    }

    setIsLocalAccount(false)
  }

  const { getFieldDecorator } = form

  return (
    <Form onSubmit={handleSubmit} className='login-form h-100-i'>
      <div className='flex-col h-100'>
        <div className='labels'>
          <div className='title'>{t('Internships management')}</div>

          <div style={{ textAlign: 'center' }}>
            {t('nurses')}&nbsp;
            <span>-</span>&nbsp;{t('doctors')}&nbsp;
            <span>-</span>&nbsp;{t('paramedics')}&nbsp;
            <span>-</span>&nbsp;{t('midwifes')}
          </div>
        </div>
        <div className='flex-fill' />
        <FormItem style={{ marginBottom: '10px' }}>
          <label>{t('common.email_address')}</label>
          {getFieldDecorator('username', { rules: [{ required: true, message: t('common.email_missing') }], valuePropName: 'defaultValue', initialValue: prefilledEmail })(
            <Input prefix={<FontAwesomeIcon icon='user' />} placeholder={t('common.email_address')} name='username' autoComplete='username' onChange={handleEmailChange} />
          )}
        </FormItem>
        {/*
          The password field is mounted from the "email" step onwards, merely hidden: the browser
          password manager parses the form when the page loads and only fills the
          username + password pair at that moment. A field injected later, after the routing, is
          not filled anymore — leaving the password to be typed by hand.
        */}
        <FormItem style={{ marginBottom: '10px', display: isLocalAccount ? undefined : 'none' }}>
          <label>{t('common.password')}</label>
          {getFieldDecorator('password', { rules: [{ required: true, message: t('login_form.password_missing') }] })(
            <Input prefix={<FontAwesomeIcon icon='lock' />} type='password' placeholder={t('common.password')} name='password' autoComplete='current-password' />
          )}
        </FormItem>
        {isLocalAccount && (
          <a href='#/' onClick={handleForgotPassword} className='forgot-password-link'>{t('login_form.forgotten_password')}</a>
        )}

        <div className='flex-fill' />

        <div className='mt-12'>
          <a className='useful-link' href='https://opalsolutions.zendesk.com/hc/fr/categories/360003066859-Comment-utiliser-Interneo-'>F.A.Q.</a>
          {t('and')}&nbsp;
          <a className='useful-link' href='https://www.youtube.com/channel/UCdF4BK3XhULkd3809NHp5pw/featured'>{t('login_form.useful_links.video')}</a>
        </div>

        {isLocalAccount && (
          <div className='sign-in-wrapper'>
            <FormItem>
              {getFieldDecorator('remember', { valuePropName: 'checked', initialValue: true })(
                <Checkbox><span className='remember-text'>{t('Remember me')}</span></Checkbox>
              )}
            </FormItem>
          </div>
        )}
        <div className='sign-in-wrapper' style={{ position: 'relative' }}>
          <Button type='primary' htmlType='submit' className='login-button' loading={loginPending || lookupPending}>
            {isLocalAccount ? t('Log in') : t('Next')}
          </Button>

          <img src='/assets/opal-logo.png' className='opal-logo-form' />
        </div>
      </div>
    </Form>
  )
}

export default Form.create()(LoginForm)
